import { useEditorState } from "@/context/EditorStateContext";
import { FileType } from "@/utils/yjs/FileSystem";
import MonacoEditor from "@monaco-editor/react";
import type * as monaco from "monaco-editor";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { MonacoBinding } from "y-monaco";
import type * as Y from "yjs";
import { useWebContainer } from "../../context/WebContainerContext";
import { Loading } from "../Loading";
import "./test.css";
import { EditorCursors } from "./EditorCursors";
import { set } from "lodash";
import { useWasmer, wasmerHomeDir } from "@/context/useWasmer";

const FILE_TYPES = {
	css: "css",
	js: "javascript",
	json: "json",
	md: "markdown",
	mjs: "javascript",
	ts: "typescript",
	py: "python",
};

export interface EditorProps {
	selectedFile: string;
}

export const Editor: React.FC<EditorProps> = ({ selectedFile }) => {
	const editorRef = useRef<monaco.editor.IStandaloneCodeEditor>();

	const [monacoEditor, setMonacoEditor] =
		useState<monaco.editor.IStandaloneCodeEditor>();

	const { syncProvider: provider, yfs } = useEditorState();
	const { instance } = useWasmer();

	const [invalidFile, setInvalidFile] = useState(false);

	// Refresh the editor mount when the stuff changes
	useEffect(() => {
		if (!monacoEditor || !selectedFile) {
			return;
		}

		// handle the editor mount
		editorRef.current = monacoEditor;

		let type: Y.Text | undefined = undefined;
		try {
			type = yfs.readFile(selectedFile).contents;
			setInvalidFile(false);
		} catch {}
		if (!type) {
			// The file is invalid. We need to handle this case
			setInvalidFile(true);
			return;
		}

		// create observer to persist file changes
		const observer = async () => {
			const type = yfs.getType(selectedFile);
			if (type === FileType.File) {
				const newValue = yfs.readFile(selectedFile).contents?.toString();
				// Clear file
				await wasmerHomeDir.removeFile(selectedFile.replace(/^\//, ""));
				// Write new contents
				await wasmerHomeDir.writeFile(
					selectedFile.replace(/^\//, ""),
					newValue ?? "",
				);
			} else if (type === FileType.Directory) {
				await wasmerHomeDir.createDir(selectedFile.replace(/^\//, ""));
			}
		};

		type.observe(observer);

		// Setup the monaco binding
		const model = editorRef.current?.getModel();
		if (model) {
			const binding = new MonacoBinding(
				type,
				model,
				new Set([editorRef.current]),
				provider?.awareness,
			);

			return () => {
				// destroying the binding
				binding.destroy();
				type.unobserve(observer);
			};
		}

		return () => {
			type.unobserve(observer);
		};
	}, [monacoEditor, selectedFile, yfs, provider]);

	return (
		<div className="h-full w-full">
			{provider ? <EditorCursors yProvider={provider} /> : null}
			{!invalidFile ? (
				<MonacoEditor
					options={{
						quickSuggestions: true,
						formatOnPaste: true,
						formatOnType: true,
						autoClosingBrackets: "languageDefined",
						minimap: {
							enabled: false,
						},
					}}
					language={
						FILE_TYPES[selectedFile.split(".").pop() as keyof typeof FILE_TYPES]
					}
					loading={<Loading message="Initializing Editor" />}
					theme="vs-dark"
					onMount={(editor, monaco) => {
						setMonacoEditor(editor);
					}}
				/>
			) : (
				<p>Invalid File</p>
			)}
		</div>
	);
};
